import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { printerCapture, putToSignedUrl } from '../api';
import { lensPending, type CalibrationSession, type LensStatus } from '../model';
import { LENS_SHOTS } from '../printers';

const POLL_MS = 3000;

/**
 * The phone page for measuring the phone's own lens (2026-10-02).
 *
 * The ChArUco board, printed full size on the bed, photographed a dozen ways
 * with the same phone, at 1x, that will photograph every sheet. Reached from
 * the printer's QR link with `/lens` on the end, so it carries the same
 * printer-scoped token and the same rules as the calibration page: the native
 * camera through the file input, the token in the URL and state only.
 *
 * The lens bends the picture by about a millimetre in the middle of the sheet,
 * which is why prints were off there; the frame corners bend most, and those
 * are where the bars sit, so the shots that fill the screen with the board
 * matter as much as the whole-board ones.
 */
export function LensCapture() {
  const { printerId = '' } = useParams();
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const api = useRef(printerCapture(printerId, token)).current;

  const [printer, setPrinter] = useState<{ name: string } | null>(null);
  const [status, setStatus] = useState<LensStatus | null>(null);
  const [session, setSession] = useState<CalibrationSession | null>(null);
  const [shot, setShot] = useState(0);
  const [sent, setSent] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const ctx = await api.context();
      setPrinter(ctx.printer);
      setStatus(ctx);
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [api]);

  useEffect(() => { void load(); }, [load]);

  const pending = status ? lensPending(status) : false;
  useEffect(() => {
    if (!(sent && pending)) return;
    const timer = setInterval(() => { void load(); }, POLL_MS);
    return () => clearInterval(timer);
  }, [sent, pending, load]);

  async function send(file: File) {
    setUploading(true);
    setError(null);
    try {
      const current = session ?? await api.lensUpload();
      if (!session) setSession(current);
      await putToSignedUrl(current.uploads[shot].url, file, 'image/jpeg');
      const next = shot + 1;
      setShot(next);
      if (next >= current.photos) setSent(true);
      await load();
    } catch (e) {
      setError(`${(e as Error).message}. If this keeps happening, start again.`);
    }
    finally { setUploading(false); }
  }

  function startAgain() {
    setSession(null);
    setShot(0);
    setSent(false);
    setError(null);
  }

  if (!token) {
    return (
      <div className="phone panel stop">
        <h2>Scan the QR code</h2>
        <p>This page needs the QR code shown on the Printers screen.</p>
      </div>
    );
  }

  const total = session?.photos ?? LENS_SHOTS.length;
  const lens = status?.lens;
  const answered = sent && !pending && !status?.lensError && lens;

  return (
    <div className="phone stack">
      <header className="panel">
        <h2>{printer?.name ?? 'Loading…'}</h2>
        <p className="muted">Measure this phone&rsquo;s lens</p>
      </header>

      <section className="panel guidance">
        <h3>Before you shoot</h3>
        <ul>
          <li>The board is printed on the bed and held down by the vacuum.</li>
          <li>
            Use <strong>this phone, at 1x</strong> &mdash; the one that will
            photograph every sheet. Do not zoom.
          </li>
          <li>
            Hold the phone <strong>at the height you photograph sheets
            from</strong> for every shot. Never move in closer: the lens
            changes as it focuses nearer, and those photos are refused.
          </li>
          <li>
            {total} photos, each as the page asks. Move the phone sideways so
            the board lands in each corner and along each edge of the screen:
            the edges are where the lens bends most and where the bars sit.
          </li>
          <li>Tap to focus on the board, keep it sharp and out of glare.</li>
        </ul>
      </section>

      {sent && pending && (
        <section className="panel">
          <p className="busy-text">
            <span className="spinner" aria-hidden />
            {status && status.lensPhotosReceived < status.lensPhotos
              ? `Waiting for the photos: ${status.lensPhotosReceived} of ${status.lensPhotos} arrived…`
              : 'Measuring the lens…'}
          </p>
        </section>
      )}

      {sent && status?.lensError && (
        <section className="panel stop">
          <h3>The lens could not be measured</h3>
          <p>{status.lensError.message}</p>
        </section>
      )}

      {answered && lens && (
        <section className="panel">
          <h3>Lens measured</h3>
          <p>
            From {lens.photosUsed} photos and {lens.corners} board corners,
            covering {Math.round(lens.coverage * 100)}% of the picture. It
            predicts a photo it was not fitted to within{' '}
            {lens.heldOutPx.toFixed(2)} pixels.
          </p>
          <p className="error">
            Now photograph the calibration print again: nothing can be printed
            on this machine until it has been measured through the new lens
            correction.
          </p>
        </section>
      )}

      {!sent && (
        <section className="panel">
          <h3>Photo {shot + 1} of {total}</h3>
          <p>{LENS_SHOTS[shot] ?? ''}</p>
          <label className="capture-button">
            <input type="file" accept="image/*" capture="environment"
                   disabled={uploading}
                   onChange={(e) => {
                     const file = e.target.files?.[0];
                     e.target.value = '';
                     if (file) void send(file);
                   }} />
            <span>{uploading ? 'Uploading…' : `Shoot photo ${shot + 1}`}</span>
          </label>
        </section>
      )}

      {(shot > 0 || sent) && (
        <section className="panel">
          <button disabled={uploading || (sent && pending)} onClick={startAgain}>
            Start again from photo 1
          </button>
        </section>
      )}

      {error && <p className="error">{error}</p>}
    </div>
  );
}
